import { useState } from "react";
import { Link, useLoaderData } from "react-router-dom";

import { Button } from "@/atoms/button/Button";
import SegmentedToggleInput from "@/form/inputs/SegmentedToggleInput/SegmentedToggleInput";
import GiftCardWalletItem from "@/modules/giftCards/GiftCardWalletItem/GiftCardWalletItem";
import { formatCurrency } from "@/toolbox";
import { get } from "api";
import { Icon } from "atoms/icon/Icon";
import ChevronDownSmall from "atoms/icon/icons/ic_chevron_down_small_ol.svg";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";
import { defineLoader } from "toolbox/Router";

type LoaderData = Awaited<ReturnType<typeof walletGiftCardsPageLoader>>;
export const walletGiftCardsPageLoader = defineLoader(async () => {
  const sentGiftCards = queryClient.fetchQuery(["/gift-cards/sent"], () =>
    get("/gift-cards/sent", {
      requestedLanguageCode: 0,
    })
  );

  const receivedGiftCards = queryClient.fetchQuery(
    ["/gift-cards/redeemed"],
    () =>
      get("/gift-cards/redeemed", {
        requestedLanguageCode: 0,
      })
  );

  return waitObj({
    sentGiftCards,
    receivedGiftCards,
  });
});

const INITIAL_VISIBLE_COUNT = 5;
const INCREMENT_COUNT = 5;
export const WalletGiftCardsPage = () => {
  const { sentGiftCards, receivedGiftCards } = useLoaderData() as LoaderData;

  const [selectedTab, setSelectedTab] = useState<"received" | "sent">(
    "received"
  );
  const [visibleCount, setVisibleCount] = useState<number>(
    INITIAL_VISIBLE_COUNT
  );

  const filteredSentGiftCards = sentGiftCards.filter(
    (card) => card.ownershipType !== "Myself"
  );

  const isReceivedEmpty = receivedGiftCards.length === 0;
  const isSentEmpty = filteredSentGiftCards.length === 0;
  const isEmpty = isReceivedEmpty && isSentEmpty;

  const currentCards =
    selectedTab === "received" ? receivedGiftCards : filteredSentGiftCards;
  const sortedCards = [...currentCards].sort(
    (a, b) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
  );

  const canShowMore = visibleCount < sortedCards.length;

  const handleTabChange = (value: string) => {
    setSelectedTab(value as "received" | "sent");
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  };

  return (
    <div className="flex h-full flex-col gap-6 px-4 pb-6 pt-4">
      {isEmpty ? (
        <EmptyGiftCardWalletList text="You don't have any gift cards yet. Browse the shop to buy your first gift card and start your journey!" />
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex justify-center">
            <SegmentedToggleInput
              name="giftCardTab"
              value={selectedTab}
              onChange={handleTabChange}
              size="xs"
              className="w-[173px]"
              options={[
                {
                  value: "received",
                  label: "Received",
                },
                {
                  value: "sent",
                  label: "Sent",
                },
              ]}
              aria-describedby="giftCardTab"
            />
          </div>
          <div className="flex flex-col items-center space-y-4">
            {sortedCards.slice(0, visibleCount).map((card) => {
              const isReceived = selectedTab === "received";
              return (
                <GiftCardWalletItem
                  key={card.giftCardTransferItemId.value}
                  giftCardTransferItemId={card.giftCardTransferItemId.value}
                  imgUrl={card.imageUrl}
                  title={`${formatCurrency(card.giftCardValue, 0)} Gift Card`}
                  personName={`${isReceived ? "From:" : "To:"} ${
                    isReceived && card.ownershipType === "Myself"
                      ? "Myself"
                      : isReceived && card.ownershipType === "Receiver"
                      ? card.senderName
                      : card.recipientName
                  }`}
                  amount={card.giftCardValue}
                />
              );
            })}
          </div>
          {canShowMore && (
            <div className="flex justify-center">
              <button
                className="flex cursor-pointer flex-col items-center pt-[6px]"
                onClick={() => {
                  setVisibleCount(
                    Math.min(visibleCount + INCREMENT_COUNT, sortedCards.length)
                  );
                }}
              >
                <p className="text-bold1 text-brand-color">Show More</p>
                <Icon svg={ChevronDownSmall} size="small" color="brandColor" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EmptyGiftCardWalletList = ({ text }: { text: string }) => {
  return (
    <div className="mx-4 flex flex-col items-center bg-white px-7 py-8">
      <div className="mb-5 text-center text-body1 text-secondary">{text}</div>

      <Link to="/shop/gift-cards">
        <Button size="small">Browse Gift Cards</Button>
      </Link>
    </div>
  );
};
